import React, { useContext, useEffect, useState } from 'react';
import { WelletContext } from '../../../context/WelletContext';
import { CircleSpinner } from 'react-spinners-kit';
import { useSelector } from 'react-redux';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { convertTime12to24, getDatePerformance, getLocale } from '../../../helpers/dates';
import { useTranslation } from 'react-i18next';
import useForm from '../../../helpers/useForm';
import ContentApp from '../../../components/ContentApp';
import ButtonProccessing from '../../../components/Common/ButtonProccessing/ButtonProccessing';
import OccasionSelector from '../../../components/ReservationCheck/OccasionSelector/OccasionSelector';
import ReservationCheckCustomerInfo from '../../../components/ReservationCheck/ReservationCheckCustomerInfo/ReservationCheckCustomerInfo';
import CustomerSpecialRequest from '../../../components/ReservationCheck/CustomerSpecialRequest/CustomerSpecialRequest';
import Header from '../../../components/Common/Header/Header';
import ErrorBox from '../../../components/Common/ErrorBox/ErrorBox';
import styles from './reservationCheck.module.css';
import { getQueryString } from '../../../helpers/url';
import '../../../components/ReservationDetail/reservation-details.css';
import EmptyActivity from '../../../components/Common/EmptyActivity/EmptyActivity';
import EventHeader from '../../../components/Common/EventHeader/EventHeader';
import ContentBottom from '../../../components/ContentBottom';
import PaxSelector from '../../../components/ReservationSelector/PaxSelector';
import PerformanceSelector from '../../../components/PerformanceSelector/PerformanceSelector';
import { format } from 'date-fns';

const ReservationInvitation = ({ history }) => {
  const ButtonStatus = {
    Normal: 'NORMAL',
    InProgress: 'INPROGRESS',
    ProgressFinished: 'FINISHED',
    Error: 'ERROR',
  };
  const { t } = useTranslation();
  const welletContext = useContext(WelletContext);
  const showId = getQueryString('showId');
  const countryCode = useSelector((state) => state.app.currentMarket.countryCode);

  const language = useSelector((state) => state.app.language);
  const [selectedButton, setSelectedButton] = useState(null);
  const [phoneError, setPhoneError] = useState('');
  const [btnStatus, setBtnStatus] = useState(ButtonStatus.Normal);
  const [error, setError] = useState(null);

  const [loading, setLoading] = useState(true);
  const [initialNoAvailabilityError, setInitialNoAvailabilityError] = useState(null);

  const [show, setShow] = useState(null);

  const getShow = async () => {
    try {
      const result = await welletContext.apis.tickets.get(`/shows/get/`, {
        params: {
          showId: showId,
          lang: language,
          details: false,
          reservationType: 'RESTAURANT',
          images: true,
          videos: false,
          IsFeature: true,
        },
      });

      setShow(result.data);
      setLoading(false);
    } catch (error) {
      console.error(error);
    }
  };

  function validate(values) {
    let errors = {};
    if (!values.customerName) {
      errors.customerName = t('nameError.EMPTY_NAME');
    } else if (/\d/.test(values.customerName)) {
      errors.customerName = t('nameError.NOT_NUMBERS');
    }

    if (!values.customerLastname) {
      errors.customerLastname = t('nameError.EMPTY_LASTNAME');
    } else if (/\d/.test(values.customerLastname)) {
      errors.customerLastname = t('nameError.NOT_NUMBERS');
    }

    if (!values.phone) {
      errors.phone = t('phoneError.EMPTY_PHONE_NUMBER');
      setPhoneError(errors.phone);
    } else {
      setPhoneError('');
    }

    if (values.phone && !isValidPhoneNumber(values.phone)) {
      errors.phone = t('phoneError.INVALID_PHONE_NUMBER');
      setPhoneError(errors.phone);
    }

    if(!values.email) {
      errors.email = t('emailError.EMPTY_EMAIL');
    }

    if (values.email && !/\S+@\S+\.\S+/.test(values.email)) {
      errors.email = t('emailError.INVALID_EMAIL');
    }

    return errors;
  }

  const sendForm = async () => {
    setBtnStatus(ButtonStatus.InProgress);

    try {
      let url = '/reservation/sendInvitation';

      const result = await welletContext.apis.tickets.post(url, {
        showId: showId,
        customer: {
          firstName: values.customerName,
          lastName: values.customerLastname,
          phone: values.phone,
          email: values.email,
        },
      });

      setBtnStatus(ButtonStatus.ProgressFinished);
      setTimeout(() => {
        history.push(
          `/reservation-success?reservationType=INVITATION`,
        );
      }, 1500);
    } catch (error) {
      setBtnStatus(ButtonStatus.Error);
      let errorMessage;
      switch (error.response.data.errorType) {
        case 'GENERIC_ERROR':
          errorMessage = t('reservationError.GENERIC_ERROR');
          break;
        case 'SHOW_NOT_FOUND':
          errorMessage = t('reservationError.SHOW_NOT_FOUND');
          break;
        case 'SCHEDULE_SOLD_OUT':
          errorMessage = t('reservationError.SCHEDULE_SOLD_OUT');
          break;
        case 'INVALID_RESERVATION_DATE':
          errorMessage = t('reservationError.INVALID_RESERVATION_DATE');
          break;
        case 'NO_AVAILABILITY':
          errorMessage = t('reservationError.NO_AVAILABILITY');
          break;
        case 'DUPLICATE_RESERVATION':
          errorMessage = t('reservationError.DUPLICATE_RESERVATION');
          break;
        case 'PURCHASE_RULE_NOT_SATISFIED':
          errorMessage = t('reservationError.PURCHASE_RULE_NOT_SATISFIED');
          break;
        default:
          errorMessage = t('reservationError.GENERIC_ERROR');
      }
      setError(errorMessage);
    }
  };

  let { values, errors, handleChange, handleSubmit } = useForm(sendForm, validate);

  useEffect(() => {
    getShow();
    handleChange(null, 'language', language);
  }, []);

  return loading ? (
    <div className='loaderDiv'>
      <CircleSpinner
        className='spinner-app mx-auto'
        style={{ margin: 'auto' }}
        size={40}
        color='var(--color-font-primary)'
      />
    </div>
  ) : (
    <>
      <Header />
      <ContentApp classes='pb-0'>
        <div className={`order-details ${styles.reservation}`}>
          {initialNoAvailabilityError ? (
            <>
              <ErrorBox title={initialNoAvailabilityError} />
              <div className='mt-4 text-center text-primary' onClick={() => history.goBack()}>
                {t('reschedule')}
              </div>
            </>
          ) : (
            <>
              <EventHeader
                eventImg={show.bannerImageUrl}
                eventName={show.name}
                eventDescription={show.city.name}
              />

              <>
                <div className='mt-4'>
                  <div className='fw-semibold text-primary font-tiny mb-3'>
                    {t('reservationInformation')}
                  </div>
                  <ReservationCheckCustomerInfo
                    handleChange={handleChange}
                    values={values}
                    errors={errors}
                    phoneError={phoneError}
                    setPhoneError={setPhoneError}
                    countryCode={countryCode}
                  />
                </div>
              </>

              {error && (
                <div className='mt-4'>
                  <>
                    <ErrorBox title={error} />
                    <div className='mt-4 text-center text-primary' onClick={() => history.goBack()}>
                      {t('reschedule')}
                    </div>
                  </>
                </div>
              )}
            </>
          )}
        </div>
      </ContentApp>
      <ContentBottom>
        <div className='mt-4'>
          <ButtonProccessing
            status={btnStatus}
            text={t('sendInvitation')}
            size='large'
            className={styles.btn}
            type='button'
            onClick={handleSubmit}
            arrow
            color='#000'
            backgroundColor='var(--color-primary)'
            iconColor='#000'
          />
        </div>
      </ContentBottom>
    </>
  );
};

export default ReservationInvitation;
